/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { convertToMysqlDate, formatFullDate, formatFullDateEndOfMonth } from '../../utils';
import { getActualTimeslots } from '../util/timeSlots';
import { getSavedCitizenship } from '../../utils/citizenshipStorage';
import {
    checkSelectionInState,
    createFilterValues,
    getBranchListBySelectedService,
    getParentServiceInfo,
    getParentSubGroupInfo,
    getPlainObject,
    getSortedPriorityList,
    isServiceForAge,
    isServiceForProfile,
} from '../util/service';

import api from '../ServiceBookingForm/services/api';

const AUTO_SELECT_SUBGROUP = true;
const AUTO_SELECT_DIRECTION = true;
const AUTO_PRIORITY = true;
const DEBUG = false;

const log = (...args) => {
    if (DEBUG) {
        // eslint-disable-next-line no-console
        console.info('->', ...args);
    }
};

export const FILTER_BY_PROFILE = false;
export const FILTER_BY_CONTROL = true;
export const MARK_ITEMS_FOR_CURRENT_PROFILE = true;

export const CLEAR_UNAVAILABLE_SELECTION = true;

const defaultPickerStartDate = formatFullDate(new Date());

const initialState = {
    filter: {
        priority: {
            branches: 4,
            directions: 1,
            subGroups: 2,
            services: 3,
        },
        branches: [],
        directions: [],
        subGroups: [],
        services: [],
    },
    branches: [],
    branchesMetadata: [],
    directions: [],
    subGroups: [],
    services: [],
    selected: {
        // branches: undefined,
        directions: undefined,
        subGroups: undefined,
        services: undefined,
        doctors: undefined,
    },
    data: [],
    doctors: [],
    selectedData: [],
    error: undefined,
    isLoading: false,
    isBranchesLoading: false,
    isDoctorsLoading: false,
    forChild: false,
    childrenAge: -1,
    declineReasons: {},
    doctorsMetadata: [],
    date: defaultPickerStartDate,
    photos: {},
    branchAutoSelect: false,
    doctorAutoSelect: false,
    bookingError: '',
    citizenshipId: 'belarus',
    preselectedData: {
        doctors: null,
        services: null,
        branches: null,
        timeslotsLoaded: false,
        timetableLoaded: false,
        reloadData: false,
    },
};

export const fetchBranches = createAsyncThunk('services/fetchBranches', async ({ baseUrl, currentBranchId }, { rejectWithValue }) => {
    try {
        const response = await api.getBranches(baseUrl);
        // storage.set('clinics', arrayToHash(response.data || [], 'branchId') || {});
        return currentBranchId ? typeof currentBranchId === 'string' ? response.data?.filter(e => e.branchId === currentBranchId) : response.data?.filter(e => currentBranchId?.some(id => id === e.branchId)) : response.data;// ?.slice(0, 1);
    } catch (err) {
        rejectWithValue(err);
        throw err;
    }
});

// eslint-disable-next-line consistent-return
export const fetchServices = createAsyncThunk('services/fetchServices', async ({ baseUrl }, { rejectWithValue }) => {
    try {
        const response = await api.getServices(baseUrl);
        return response.data;
    } catch (err) {
        rejectWithValue(err);
        throw err;
    }
});

export const fetchDoctors = createAsyncThunk('services/fetchDoctors', async ({ baseUrl, currentBranchId }, { rejectWithValue }) => {
    try {
        const response = await api.getDoctors(baseUrl);
        return currentBranchId ? typeof currentBranchId === 'string' ? response.data.filter(e => e.branches.some(b => b === currentBranchId)) : response.data.filter(e => e.branches.some(b => currentBranchId?.some(id => id === b))) : response.data;
    } catch (err) {
        rejectWithValue(err);
        throw err;
    }
});

export const fetchServicesTimeslots = createAsyncThunk(
    'services/fetchServicesTimeslots',
    async ({
        baseUrl,
        date,
        serviceId,
        branchId,
        clearSelectedDoctor = false,
    }, { rejectWithValue }) => {
        try {
            // console.log('FETCH TIMESLOTS', {
            //     date,
            //     serviceId,
            //     branchId,
            // });
            const response = await api.getTimeslotsForServices(baseUrl, date, serviceId, branchId);
            return {
                data: response.data,
                serviceId,
                branchId,
                clearSelectedDoctor,
            };
        } catch (err) {
            rejectWithValue(err);
            throw err;
        }
    },
);

export const fetchServicesTimetable = createAsyncThunk(
    'services/fetchServicesTimetable',
    // eslint-disable-next-line consistent-return
    async ({
        baseUrl,
        assignment,
        doctorId,
        dateStart,
        dateEnd,
    }, { rejectWithValue }) => {
        try {
            // console.log('FETCH TIMETABLE', {
            //     assignment,
            //     doctorId,
            //     dateStart,
            //     dateEnd,
            // });
            const response = await api.getDoctorTimeTable(baseUrl, assignment, convertToMysqlDate(dateStart), convertToMysqlDate(dateEnd ?? formatFullDateEndOfMonth(new Date())));
            const data = response?.data ?? [];
            const allData = new Set([...data.map(item => formatFullDate(item?.startTime))]);
            return {
                data: Array.from(allData),
                assignment,
                doctorId,
                richData: response?.data,
            };
        } catch (err) {
            return rejectWithValue(
                {
                    assignment,
                    doctorId,
                    data: [],
                    richData: [],
                    isNetwork: err?.code === 'ERR_NETWORK',
                },
                err,
            );
            // return {};
        }
    },
);

const getInitialState = () => {
    const state = {
        ...initialState,
        citizenshipId: getSavedCitizenship([ 'belarus', 'foreign' ]),
    };
    const priority = getSortedPriorityList(state?.filter?.priority ?? {});
    const selection = state.selected;
    const tempData = [...(state?.data ?? [])];
    const filter = createFilterValues({
        priority,
        data: tempData,
        filter: state?.filter,
        exclude: [],
        selection,
        branches: state.branches,
        isBranchesVisible: false,
        doctors: [],
        forChild: false,
        age: -1,
    });
    state.selectedData = filter.selectedData;
    state.filter = { ...state.filter, ...filter.filter };
    state.date = formatFullDate(new Date());
    state.activeTimeslotId = '';
    state.timeslots = {};
    return state;
};

const getFilterFromState = (state, exclusion = [], withoutAutoselect) => {
    const selection = state.selected;
    const tempData = [...(state?.data ?? [])];
    return createFilterValues({
        priority: getSortedPriorityList(state.filter.priority),
        data: tempData,
        filter: state?.filter,
        exclude: exclusion,
        selection,
        branches: state.branches,
        profile: state.selectedProfile,
        doctors: state.doctors,
        forChild: state.forChild,
        age: state.childrenAge,
        withoutAutoselect,
    });
};

// eslint-disable-next-line max-statements
const checkSelection = (state) => {
    const {
        selected,
        filter,
    } = state;
    const result = { ...selected };
    try {
        const direction = filter?.directions?.find(({ id }) => id === result?.directions);
        if (!direction || !direction.forProfile || !direction.hasDoctors) {
            result.directions = undefined;
            result.subGroups = undefined;
            result.services = undefined;
            result.doctors = undefined;
            result.branches = undefined;
            return result;
        }
        const subGroup = filter?.subGroups?.find(({ id }) => id === result?.subGroups);
        if (!subGroup || !subGroup.forProfile || !subGroup.hasDoctors || subGroup.hidden) {
            result.subGroups = undefined;
            result.services = undefined;
            result.branches = undefined;
            result.doctors = undefined;
            return result;
        }
        const service = filter?.services?.find(({ id }) => id === result?.services);
        if (!service || !service?.forProfile || !service.hasDoctors || service.hidden) {
            result.services = undefined;
            result.branches = undefined;
            result.doctors = undefined;
            return result;
        }
        const branch = filter?.branches?.find(({ id }) => id === result?.branches);
        if (!branch) {
            result.branches = undefined;
        }

        if (result.doctors) {
            const selectedDoctorObj = state?.doctors?.find(d => d.doctorId === result.doctors);
            if (selectedDoctorObj) {
                const age = state.childrenAge ?? -1;
                const isValid = (!(FILTER_BY_PROFILE || FILTER_BY_CONTROL) || isServiceForProfile({
                    age,
                    serviceForAdult: selectedDoctorObj.adult,
                    serviceForChild: selectedDoctorObj.children,
                    serviceAge: selectedDoctorObj.children_age,
                    serviceAgeTo: selectedDoctorObj.children_age_to,
                    child: state.forChild,
                }));
                if (!isValid) {
                    result.doctors = undefined;
                }
            }
        }
    } catch {
        //
    }
    return result;
};

const getFilterDataBySelection = (state, action) => {
    const result = {
        selectedData: undefined,
        filter: undefined,
    };
    const priority = getSortedPriorityList(state?.filter?.priority ?? {});
    const selection = state.selected;
    const tempData = [...(state?.data ?? [])];
    const exclusion = action?.payload?.exclude ?? [];
    const filter = createFilterValues({
        priority,
        data: tempData,
        filter: state?.filter,
        exclude: exclusion,
        selection,
        branches: state.branches,
        profile: state.selectedProfile,
        doctors: state.doctors,
        forChild: state.forChild,
        age: state.childrenAge,
    });
    result.selectedData = filter.selectedData;
    result.filter = { ...state.filter, ...filter.filter };
    return result;
};

export const servicesSlice = createSlice({
    name: 'services',
    initialState: getInitialState,
    reducers: {
        selectCitizenship: (state, action) => {
            state.citizenshipId = action.payload;
        },
        selectBranch: (state, action) => {
            log('selectBranch', action?.payload);
            state.branchAutoSelect = !(action.payload === '' || action.payload === null || action.payload === undefined);
            state.doctorAutoSelect = !(action.payload === '' || action.payload === null || action.payload === undefined);
            // state.bookingError = null;
            state.selected.branches = action?.payload ?? '';
            state.selected.doctors = undefined;
            state.activeTimeslotId = '';
        },
        selectBranchSimplified: (state, action) => {
            log('selectBranch2', action?.payload);
            state.branchAutoSelect = !(action.payload === '' || action.payload === null || action.payload === undefined);
            state.doctorAutoSelect = !(action.payload === '' || action.payload === null || action.payload === undefined);
            state.selected.branches = action?.payload ?? '';
            state.activeTimeslotId = '';
        },
        // eslint-disable-next-line max-statements
        selectService: (state, action) => {
            log('selectService', action?.payload);
            const serviceId = action?.payload;
            // state.bookingError = null;
            state.selected.branches = null;
            if (AUTO_PRIORITY) {
                state.filter.priority = {
                    directions: 1,
                    subGroups: 2,
                    services: 3,
                    branches: 4,
                };
                if (serviceId) {
                    const selectedParents = getParentServiceInfo(state.data, serviceId);
                    state.selected = { ...state.selected, ...selectedParents };
                    state.selected.services = serviceId;
                    const filter = getFilterFromState(state, action?.payload?.exclude ?? [], !action?.payload);
                    state.selectedData = filter.selectedData;
                    state.filter = { ...state.filter, ...filter.filter };
                    if (serviceId) {
                        state.isBranchesVisible = true;
                    }
                }
            } else if (AUTO_SELECT_SUBGROUP) {
                const selectedParents = getParentServiceInfo(state.data, serviceId);
                state.selected = { ...state.selected, ...selectedParents };
            }
            state.filter.branches = getBranchListBySelectedService(state);

            state.selected.services = serviceId;
            if (CLEAR_UNAVAILABLE_SELECTION) {
                state.selected = checkSelectionInState(state);
            }
            if (state?.preselectedData?.branches) {
                if (state.filter.branches.find(({ id }) => id === state.preselectedData?.branches)) {
                    state.selected.branches = state.preselectedData?.branches;
                }
            } else {
                state.branchAutoSelect = true;
                state.selected.branches = null;
            }
            if (state.preselectedData?.doctors) {
                const services = state;
                const branchId = services.selected.branches;
                const id = services.selected.services;
                const age = services?.childrenAge ?? -1;
                let doctors = [];
                if (branchId) {
                    doctors = services?.doctors
                        ?.filter(
                            ({
                                branches,
                                children: forChildren,
                                adult: forAdult,
                                children_age: childrenAge,
                                children_age_to: childrenAgeTo,
                            }) => (!(FILTER_BY_PROFILE || FILTER_BY_CONTROL) ||
                                    isServiceForProfile({
                                        age,
                                        serviceForAdult: forAdult,
                                        serviceForChild: forChildren,
                                        serviceAge: childrenAge,
                                        serviceAgeTo: childrenAgeTo,
                                        child: services?.forChild
                                    })) &&
                                branches?.includes(branchId),
                        )
                        .map(doctor => ({
                            ...doctor,
                            id1: doctor?.doctorId,
                            name: doctor?.fullName,
                            serviceMappings: doctor?.worksAt?.find(({ branchId: _id }) => _id === branchId).serviceMappings,
                        }))
                        .filter(({ serviceMappings }) => serviceMappings?.find(({ serviceId: sid }) => sid === id));
                }
                if (doctors.find(({ id1 }) => id1 === state.preselectedData?.doctors)) {
                    state.selected.doctors = state.preselectedData?.doctors;
                }
            } else {
                state.doctorAutoSelect = true;
                state.selected.doctors = null;
            }
            state.timeslots = [];
            state.timetable = {};
        },
        selectServiceSimplified: (state, action) => {
            const serviceId = action?.payload;
            if (serviceId) {
                const selectedParents = getParentServiceInfo(state.data, serviceId);
                state.selected = { ...state.selected, ...selectedParents };
                state.selected.services = serviceId;
                const filter = getFilterFromState(state, action?.payload?.exclude ?? [], !action?.payload);
                state.selectedData = filter.selectedData;
                state.filter = { ...state.filter, ...filter.filter };
            } else {
                state.selected.services = null;
            }
        },
        selectDirection: (state, action) => {
            log('selectDirection', action?.payload);
            state.doctorAutoSelect = true;
            state.branchAutoSelect = true;
            state.selected.directions = action?.payload;
            // state.bookingError = null;
            state.selected.subGroups = null;
            state.selected.services = null;
            state.selected.branches = null;
            state.timeslots = [];
            state.declineReasons = {};

            state.selected.doctors = null;
            state.selected.branches = null;
            const filter = getFilterFromState(state, action?.payload?.exclude ?? [], !action?.payload);
            state.selectedData = filter.selectedData;
            state.filter = { ...state.filter, ...filter.filter };
            state.filter.branches = getBranchListBySelectedService(state);
            if (CLEAR_UNAVAILABLE_SELECTION) {
                state.selected = checkSelectionInState(state);
            }
        },
        selectSubGroup: (state, action) => {
            log('selectSubGroup', action?.payload);
            state.doctorAutoSelect = true;
            state.branchAutoSelect = true;
            const subGroupId = action?.payload;
            state.selected.subGroups = subGroupId;
            // state.bookingError = null;
            if (AUTO_SELECT_DIRECTION && subGroupId) {
                state.selected = { ...state.selected, ...getParentSubGroupInfo(state.data, subGroupId) };
            }
            state.selected.services = null;
            state.selected.branches = null;
            state.selected.doctors = null;
            state.timeslots = [];
            state.declineReasons = {};

            const filter = getFilterFromState(state, action?.payload?.exclude ?? [], !action?.payload);
            state.selectedData = filter.selectedData;
            state.filter = { ...state.filter, ...filter.filter };
            state.filter.branches = getBranchListBySelectedService(state);
            if (CLEAR_UNAVAILABLE_SELECTION) {
                state.selected = checkSelectionInState(state);
            }
        },
        selectForChild: (state, action) => {
            log('selectForChild', action?.payload);
            state.doctorAutoSelect = true;
            state.branchAutoSelect = true;
            state.forChild = action.payload;
            // state.bookingError = null;
            if (!state.forChild) {
                state.childrenAge = -1;
            }
            const filter = getFilterFromState(state, action?.payload?.exclude ?? []);
            state.selectedData = filter.selectedData;
            state.filter = { ...state.filter, ...filter.filter };
            state.selected = checkSelection(getPlainObject(state));
            // state.selected.branches = undefined;
            state.declineReasons = {};
        },
        selectChildrenAge: (state, action) => {
            log('selectChildrenAge', action?.payload);
            state.doctorAutoSelect = true;
            state.branchAutoSelect = true;
            state.childrenAge = Number(action.payload ?? -1);
            // state.bookingError = null;
            const filter = getFilterFromState(state, action?.payload?.exclude ?? []);
            state.selectedData = filter.selectedData;
            state.filter = { ...state.filter, ...filter.filter };
            state.selected = checkSelection(getPlainObject(state));
        },
        selectChildrenFlag: (state, action) => {
            log('selectChildrenFlag', action?.payload);
            state.bookingError = '';
            state.doctorAutoSelect = true;
            state.branchAutoSelect = true;
            state.forChild = action.payload.forChild;
            // state.bookingError = null;
            state.childrenAge = Number(action.payload.forChild ? action.payload.childrenAge ?? -1 : -1);
            const filter = getFilterFromState(state, action?.payload?.exclude ?? []);
            state.selectedData = filter.selectedData;
            state.filter = { ...state.filter, ...filter.filter };
            state.selected = checkSelection(getPlainObject(state));
        },
        setFilterBySelections: (state, action) => {
            log('setFilterBySelections', action?.payload);
            const fd = getFilterDataBySelection(state, action);
            state.selectedData = fd.selectedData;
            state.filter = { ...state.filter, ...fd.filter };
        },
        resetSelections: (state) => {
            log('resetSelections');
            state.doctorAutoSelect = true;
            state.branchAutoSelect = true;
            state.selected.branches = undefined;
            state.selected.directions = undefined;
            state.selected.subGroups = undefined;
            state.selected.services = undefined;
            state.isBranchesVisible = false;
            state.declineReasons = {};
            state.forChild = false;
            state.childrenAge = -1;
            // state.bookingError = null;
        },
        selectDoctor: (state, action) => {
            log('selectDoctor', action?.payload);
            const doctor = action.payload;
            // state.bookingError = null;
            state.selected.doctors = doctor;
            state.doctorAutoSelect = !(doctor === undefined || doctor === '');
            state.branchAutoSelect = !(doctor === undefined || doctor === '');
            state.activeTimeslotId = '';
        },
        selectDoctorSimplified: (state, action) => {
            const doctor = action.payload;
            state.doctorAutoSelect = !(doctor === undefined || doctor === '');
            state.selected.doctors = doctor;
            state.activeTimeslotId = '';
        },
        setDeclineReasons: (state, action) => {
            log('setDeclineReasons', action?.payload);
            const {
                assignmentId,
                reasons,
            } = action.payload;
            if (!assignmentId) {
                return;
            }
            if (!reasons || !Object.keys(reasons)?.length) {
                return;
            }
            state.declineReasons[assignmentId] = reasons; // mergeObjects(state.declineReasons[assignmentId] ?? {}, reasons);
            // state.declineReasons[assignmentId] = mergeObjects(state.declineReasons[assignmentId] ?? {}, reasons);
        },
        removeDeclineReasons: (state, action) => {
            log('removeDeclineReasons', action?.payload);
            const {
                assignmentId,
                profileId,
            } = action.payload;
            if (!assignmentId) {
                state.declineReasons = {};
            } else {
                const temp = state.declineReasons[assignmentId] ?? {};
                delete temp[profileId];
                state.declineReasons[assignmentId] = temp;
            }
        },
        clearDeclineReasons: (state) => {
            log('clearDeclineReasons');
            state.declineReasons = {};
        },
        selectDate: (state, action) => {
            log('selectDate', action?.payload);
            state.date = action.payload ?? '';
            state.activeTimeslotId = '';
            state.doctorAutoSelect = true;
            state.branchAutoSelect = true;

            // state.bookingError = null;
        },
        resetTimetable: (state) => {
            log('resetTimetable');
            state.timetable = [];
        },
        resetDate: (state) => {
            log('resetDate');
            state.date = defaultPickerStartDate;
            state.activeTimeslotId = '';
        },
        setActiveTimeslotId: (state, action) => {
            log('setActiveTimeslotId', action?.payload);
            state.activeTimeslotId = action.payload;
            // state.bookingError = null;
        },
        setSelectedSlotDuration: (state, action) => {
            log('setSelectedSlotDuration', action?.payload);
            state.selected.slotDuration = action.payload;
        },
        setSelectedServiceName: (state, action) => {
            log('setSelectedServiceName', action?.payload);
            state.selected.serviceName = action.payload;
        },
        setBranchAutoSelect: (state, action) => {
            log('setBranchAutoSelect', action?.payload);
            state.branchAutoSelect = action.payload;
        },
        setDoctorAutoSelect: (state, action) => {
            log('setDoctorAutoSelect', action?.payload);
            state.doctorAutoSelect = action.payload;
        },
        setBookingError: (state, action) => {
            log('setBookingError', action?.payload);
            state.bookingError = action.payload;
        },
        setPreselectedData: (state, action) => {
            log('setPreselectedData', action?.payload);
            state.preselectedData = { ...state.preselectedData, ...action.payload };
        },
        doFilterBranches: (state) => {
            const branches = getBranchListBySelectedService(state);
            if (branches?.length !== state?.filter?.branches?.length) {
                state.filter.branches = branches;
            } else if (state.filter.branches?.every?.(b => branches?.find?.(b1 => b1.id === b.id))) {
                return;
            }
            state.filter.branches = getBranchListBySelectedService(state);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchBranches.pending, (state) => {
                state.isBranchesLoading = true;
                state.isLoading = true;
                state.error = undefined;
            })
            .addCase(fetchBranches.rejected, (state, { error }) => {
                state.error = error;
                state.isBranchesLoading = false;
                state.isLoading = false;
            })
            .addCase(fetchBranches.fulfilled, (state, action) => {
                state.branchesData = action.payload;
                state.branches = action.payload?.map(({
                    branchId,
                    branch,
                }) => ({
                    id: branchId,
                    name: branch,
                }));
                state.error = undefined;
                state.isBranchesLoading = false;
                state.isLoading = false;
                const filter = getFilterFromState(state, action?.payload?.exclude ?? []);
                state.selectedData = filter.selectedData;
                state.filter = { ...state.filter, ...filter.filter };
                state.filter.branches = getBranchListBySelectedService(state);
            })
            .addCase(fetchServices.pending, (state) => {
                state.isServicesLoading = true;
                state.isLoading = true;
                state.error = undefined;
            })
            .addCase(fetchServices.rejected, (state, { error }) => {
                state.error = error;
                state.isServicesLoading = false;
                state.isLoading = false;
            })
            .addCase(fetchServices.fulfilled, (state, action) => {
                state.servicesData = action.payload;
                state.error = undefined;
                state.isServicesLoading = false;
                state.isLoading = false;
                state.data = action.payload;
                state.isBranchesVisible = false;

                const fd = getFilterDataBySelection(state, action);
                state.selectedData = fd.selectedData;

                state.filter = { ...state.filter, ...fd.filter };
                state.filter.branches = getBranchListBySelectedService(state);
            })
            .addCase(fetchDoctors.fulfilled, (state, action) => {
                state.doctors = action.payload;
                state.isDoctorsLoading = false;
                const filter = getFilterFromState(state);
                state.selectedData = filter.selectedData;
                state.filter = { ...state.filter, ...filter.filter };
                state.filter.branches = getBranchListBySelectedService(state);
                // }
            })
            .addCase(fetchDoctors.pending, (state) => {
                state.isDoctorsLoading = true;
            })
            .addCase(fetchDoctors.rejected, (state) => {
                state.isDoctorsLoading = false;
                state.doctors = [];
            })
            .addCase(fetchServicesTimeslots.pending, (state) => {
                state.isLoading = true;
                state.error = undefined;
            })
            .addCase(fetchServicesTimeslots.rejected, (state, { error }) => {
                state.error = error;
                state.isLoading = false;
            })
            .addCase(fetchServicesTimeslots.fulfilled, (state, action) => {
                const branchId = action.payload?.branchId;
                const timeslots = action.payload.data?.map(doctor => ({
                    ...doctor,
                    serviceMappings: doctor?.worksAt?.find(({ branchId: _id }) => _id === branchId).serviceMappings,
                }));

                if (action?.payload?.clearSelectedDoctor) {
                    const isDoctor = action?.payload?.data?.find(({ doctorId }) => doctorId === state.selected.doctors);
                    if (!isDoctor) {
                        state.selected.doctors = '';
                    }
                }
                console.log('timeslots: ', timeslots);
                state.isLoading = false;

                try {
                    const photos = (action?.payload?.data ?? [])
                        .filter(({
                            doctorId,
                            photoLink,
                        }) => doctorId && photoLink)
                        .reduce(
                            (prev, current) => ({
                                ...prev,
                                [current?.doctorId]: current?.photoLink,
                            }),
                            {},
                        );
                    state.photos = { ...(state.photos ?? {}), ...photos };
                } catch (e) {
                    //
                }

                const freeTimeslots = getActualTimeslots(timeslots, action.payload.serviceId);
                state.timeslots = {
                    data: freeTimeslots,
                    serviceId: action.payload.serviceId,
                };
                console.log('freeTimeslots: ', freeTimeslots);
                state.error = undefined;
                state.isLoading = false;
                if (!state.preselectedData.timeslotsLoaded) {
                    state.preselectedData.timeslotsLoaded = true;
                }
                state.filter.branches = getBranchListBySelectedService(state);
            })
            .addCase(fetchServicesTimetable.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchServicesTimetable.rejected, (state, {
                error,
                payload,
            }) => {
                state.timetable = {
                    ...state.timetable,
                    [payload.doctorId]: [],
                };
                if (!payload.isNetwork) {
                    state.timetable = {
                        ...state.timetable,
                        [payload.doctorId]: [],
                    };
                    state.richTimetable = {
                        ...state.richTimetable,
                        [payload.doctorId]: [],
                    };
                } else {
                    state.timetable = { ...state.timetable };
                    state.richTimetable = { ...state.richTimetable };
                }
                state.isLoading = false;
                state.error = error;
            })
            .addCase(fetchServicesTimetable.fulfilled, (state, action) => {
                state.isLoading = false;
                state.error = null;
                state.timetable = { [action.payload.doctorId]: action.payload.data ?? [] };
                state.richTimetable = { [action.payload.doctorId]: action.payload.richData ?? [] };
                state.assignment = action.payload.assignment;
                if (!state.preselectedData.timetableLoaded) {
                    state.preselectedData.timetableLoaded = true;
                }
            });
    },
});

export const {
    selectCitizenship,
    selectBranch,
    selectDirection,
    selectSubGroup,
    selectService,
    setFilterBySelections,
    resetSelections,
    selectChildrenAge,
    selectForChild,
    setDeclineReasons,
    removeDeclineReasons,
    clearDeclineReasons,
    selectChildrenFlag,
    selectDoctor,
    selectDate,
    setActiveTimeslotId,
    setSelectedServiceName,
    setSelectedSlotDuration,
    setDoctorAutoSelect,
    setBranchAutoSelect,
    setBookingError,
    resetTimetable,
    setPreselectedData,
    doFilterBranches,
    selectBranchSimplified,
    selectDoctorSimplified,
    selectServiceSimplified,
} = servicesSlice.actions;

export default servicesSlice.reducer;
